import React, { useState } from "react";
import styled, { keyframes } from "styled-components"; // Import keyframes
import NavbarAdmin from "../../Components/Admin/NavbarAdmin";
import Sidebar from "../../Components/Admin/Sidebar";
import useFetchOrders from "../../hooks/Admin/useOrders";
import useFetchProducts from "../../hooks/Admin/useProduct";
import useUsers from "../../hooks/Admin/useGetUsers";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import logo from "../../Assets/logo.png"; // Import the logo for the loading animation

const Dashboard = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const { orders, loading: ordersLoading, error: ordersError } = useFetchOrders();
  const { products, loading: productsLoading, error: productsError } = useFetchProducts();
  const { users, loading: usersLoading, error: usersError } = useUsers();

  const totalOrders = orders ? orders.length : 0;
  const totalUsers = users ? users.length : 0;

  // Dynamic chart data for products by category (separated by category)
  const getCategoryData = (category) => {
    if (!products || !orders) return [];

    const categoryProducts = products.filter((p) => (p.category || "Uncategorized").toLowerCase() === category.toLowerCase());
    return categoryProducts.map((product) => {
      const categoryOrders = orders.filter((o) => o.product_id === product.id);
      const totalSold = categoryOrders.reduce((sum, o) => sum + (o.quantity || 0), 0);

      console.log(`Product: ${product.product_name}, Category: ${category}, Total Sold: ${totalSold}, Orders:`, categoryOrders);

      return { name: product.product_name, value: totalSold };
    });
  };

  const categoryData = {
    fruits: getCategoryData("fruits"),
    food: getCategoryData("food"),
    vegetables: getCategoryData("vegetables"),
  };

  const isLoading = ordersLoading || usersLoading || productsLoading;
  const hasError = ordersError || usersError || productsError;

  return (
    <Container>
      <NavbarAdmin setIsSidebarOpen={setIsSidebarOpen} />
      <Main>
        <Sidebar isOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />
        <Content isSidebarOpen={isSidebarOpen}>
          <TitleContainer>
            <Title>DASHBOARD</Title>
          </TitleContainer>

          {isLoading ? (
            <LoadingPlaceholder />
          ) : hasError ? (
            <ErrorMessage>Error: {hasError}</ErrorMessage>
          ) : (
            <>
              {/* Stats Section */}
              <StatsContainer>
                <StatCard>Orders: <strong>{totalOrders}</strong></StatCard>
                <StatCard>Customers: <strong>{totalUsers}</strong></StatCard>
              </StatsContainer>

              {/* Charts Grid */}
              <Grid>
                <ChartCard>
                  <h3>Fruits</h3>
                  <BarChart width={300} height={200} data={categoryData.fruits}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#FFBB28" />
                  </BarChart>
                </ChartCard>

                <ChartCard>
                  <h3>Food</h3>
                  <BarChart width={300} height={200} data={categoryData.food}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#00C49F" />
                  </BarChart>
                </ChartCard>

                <ChartCard>
                  <h3>Vegetables</h3>
                  <BarChart width={300} height={200} data={categoryData.vegetables}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#0088FE" />
                  </BarChart>
                </ChartCard>
              </Grid>

              {/* Pie Chart in Center (optional, can be removed if not needed) */}
              <PieWrapper>
                <PieChart width={350} height={350}>
                  <Pie
                    data={Object.values(categoryData).flat().map((item) => ({ name: item.name, value: item.value }))}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label
                  >
                    {Object.values(categoryData).flat().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={["#0088FE", "#00C49F", "#FFBB28", "#FF8042"][index % 4]} />
                    ))}
                  </Pie>
                  <Legend />
                </PieChart>
              </PieWrapper>
            </>
          )}
        </Content>
      </Main>
    </Container>
  );
};

// --- LOADING PLACEHOLDER ---
const LoadingPlaceholder = () => (
  <div style={styles.loadingPlaceholder}>
    <div style={styles.loadingSpinner}>
      <LogoSpinnerContainer>
        <LoadingLogo src={logo} alt="Loading Logo" />
        <InnerSpinner />
      </LogoSpinnerContainer>
      <LoadingText>LOADING</LoadingText>
    </div>
  </div>
);

// --- STYLED COMPONENTS ---
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #eaf4e1;
`;

const Main = styled.div`
  display: flex;
  flex: 1;
`;

const Content = styled.div`
  padding: 20px;
  width: ${({ isSidebarOpen }) => (isSidebarOpen ? "calc(100% - 240px)" : "calc(100% - 100px)")};
  margin-left: ${({ isSidebarOpen }) => (isSidebarOpen ? "200px" : "60px")};
  transition: margin-left 0.3s ease-in-out;
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ccc;
  padding-bottom: 10px;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: bold;
  color: #21551c;
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
`;

const StatCard = styled.div`
  background: #ffffff;
  padding: 15px 20px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  flex: 1;
  font-size: 18px;
  font-weight: bold;
  color: #333;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
`;

const ChartCard = styled.div`
  background: #ffffff;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const PieWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  background: #ffffff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const ErrorMessage = styled.p`
  text-align: center;
  font-size: 18px;
  color: red;
`;

const LogoSpinnerContainer = styled.div`
  position: relative;
  width: 50px;
  height: 50px;
`;

const LoadingLogo = styled.img`
  width: 50px;
  height: 50px;
  z-index: 1;
  border-radius: 50%;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const InnerSpinner = styled.div`
  position: absolute;
  top: -5px;
  left: -5px;
  width: 50px;
  height: 50px;
  border: 5px solid rgba(255, 255, 255, 0.5);
  border-top: 3px solid #004d00;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  pointer-events: none;
`;

const LoadingText = styled.span`
  color: #004d00;
  font-size: 1.2rem;
  font-weight: bold;
  margin-top: 10px;
  display: block;
  animation: flash 1.5s infinite;
  &::after {
    content: ".";
    animation: dots 1.5s steps(3, end) infinite;
    margin-left: 5px;
  }
  @keyframes flash {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
  @keyframes dots {
    0% { content: "."; }
    33% { content: ".."; }
    66% { content: "..."; }
    100% { content: "."; }
  }
  @media (max-width: 768px) {
    font-size: 1rem;
    animation: flash 1.2s infinite;
  }
`;

const styles = {
  loadingPlaceholder: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "50vh",
    gap: "10px",
    width: "100%",
  },
  loadingSpinner: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
  },
};

export default Dashboard;